import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useProfileTaggedPhotos } from "@/queries/event-gallery";
import { GalleryLightbox } from "./GalleryLightbox";

/**
 * A profile's Photos tab: thumbnails of every approved gallery photo the user
 * is tagged in, newest first. A tile opens the shared gallery lightbox, which
 * carries the uploader and tags.
 */
export function ProfilePhotosTab({ username }: { username: string }) {
  const { data, isLoading, error } = useProfileTaggedPhotos(username);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div
        data-testid="profile-photos-skeleton"
        className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4"
      >
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} className="aspect-square w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {error.message}
        </CardContent>
      </Card>
    );
  }

  const photos = data ?? [];
  if (photos.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No tagged photos yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {photos.map((photo, index) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setActiveIndex(index)}
            aria-label={`View photo ${index + 1}`}
            className="aspect-square overflow-hidden rounded-md bg-muted focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
          >
            <img
              src={photo.thumbnail_url}
              alt=""
              loading="lazy"
              className="size-full object-cover transition-transform hover:scale-105"
            />
          </button>
        ))}
      </div>
      <GalleryLightbox photos={photos} index={activeIndex} onIndexChange={setActiveIndex} />
    </>
  );
}
